import { useEffect, useState } from 'react';

import { debounce, uniqBy } from 'lodash-es';

import { useAppSelector } from 'src/app/store/hooks';
import {
  usePassengersRecentPayersListQuery,
  usePayersListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { useUserRoles } from 'src/features/auth/useUserRoles';

export function usePayers({
  selectedPassengerId,
  refetchOnMountOrArgChange,
  pageSize,
}: {
  selectedPassengerId?: string;
  refetchOnMountOrArgChange?: boolean;
  pageSize?: number;
} = {}) {
  const { isRideOrderingPortalUser } = useUserRoles();
  const { selectedTrip } = useAppSelector((state) => state.addATrip);
  const { selectedTripSubscription } = useAppSelector(
    (state) => state.tripSubscription
  );

  const [passengerSearchTerm, setPassengerSearchTerm] = useState('');

  const [payerOptions, setPayerOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const { data: payers, isLoading: isPayersLoading } = usePayersListQuery(
    { search: passengerSearchTerm, isActive: true, pageSize },
    { refetchOnMountOrArgChange }
  );

  const [payersWithSelectedPayer, setPayersWithSelectedPayer] =
    useState<typeof payers>();

  const { data: recentPayers, isLoading: isRecentPayersLoading } =
    usePassengersRecentPayersListQuery(
      { id: selectedPassengerId || '' },
      { skip: !selectedPassengerId || isRideOrderingPortalUser }
    );

  const handleQueryChange = (query: string) => {
    setPassengerSearchTerm(query);
  };

  const debounceSearch = debounce(handleQueryChange, 300);

  useEffect(() => {
    const allOptions =
      payers?.results.map((payer) => ({
        label: payer.payer_name
          ? payer.is_active
            ? payer.payer_name
            : `${payer.payer_name} (Inactive)`
          : '',
        value: payer.id,
        disabled: !payer.is_active,
      })) || [];

    // the request only returns top 100 results, so we need to add
    // the selected payer to the list incase its not in the top 100
    // any duplicate payers will be removed by uniqBy
    // TODO: Maybe we should just use the list-selected endpoint for this now
    // Chose not to for now because this is a smaller change and easier to write a regression test for
    if (selectedTrip?.payer) {
      allOptions.push({
        label: selectedTrip.payer.is_active
          ? `${selectedTrip.payer.payer_name}`
          : `${selectedTrip.payer.payer_name} (Inactive)`,
        value: selectedTrip.payer.id,
        disabled: !selectedTrip.payer.is_active,
      });
    }

    if (payers && selectedTrip) {
      setPayersWithSelectedPayer({
        ...payers,
        results: uniqBy([...payers.results, selectedTrip.payer], 'id'),
      });
    }

    // same as above, but for the selected trip subscription payer
    if (selectedTripSubscription?.payer) {
      allOptions.push({
        label: `${selectedTripSubscription.payer.payer_name}`,
        value: selectedTripSubscription.payer.id,
        disabled: !selectedTripSubscription.payer.is_active,
      });
    }

    if (payers && selectedTripSubscription) {
      setPayersWithSelectedPayer({
        ...payers,
        results: uniqBy(
          [...payers.results, selectedTripSubscription.payer],
          'id'
        ),
      });
    }

    const recentOptions =
      recentPayers?.results
        .filter((payer) => payer.payer_organization.is_active === true)
        .map((payer) => ({
          label: `${payer.payer_organization.payer_name} (Recent)`,
          value: payer.payer_organization.id,
        })) || [];

    setPayerOptions(uniqBy([...recentOptions, ...allOptions], 'value'));
  }, [payers, recentPayers, selectedTrip, selectedTripSubscription]);

  return {
    payers: payersWithSelectedPayer || payers,
    recentPayers,
    payerOptions,
    debounceSearch,
    setPassengerSearchTerm,
    isLoading: isPayersLoading || isRecentPayersLoading,
  };
}
